/**
 * Paint GeoJSON/DXF import layer previews onto an existing Leaflet map
 * (main /maps map or the standalone import-layer-map-preview).
 */

import * as L from 'leaflet';
import type { ResolvedImportSourceCrs } from '@core/shared-bridge';
import { environment } from '../../../environments/environment';
import {
  buildImportPreviewFeatureCollection,
  filterPreviewFeaturesByLayer,
  importLayerNameFromFeature,
  importLayerPreviewColor,
  resolveImportPreviewCrs,
  type ImportPreviewFeatureCollection,
} from './import-geometry-preview.util';

export interface ImportOverlayLayerMeta {
  layerName: string;
  featureCount?: number;
  visible?: boolean;
  visibleOnMap?: boolean;
}

export interface ImportLayerMapOverlayOptions {
  /** Fit padding when a side panel covers part of the map (px). */
  fitPaddingBottomRight?: [number, number];
  fitPaddingTopLeft?: [number, number];
  maxFeatures?: number;
  onLayerActivated?: (layerName: string) => void;
}

export class ImportLayerMapOverlayHost {
  private overlayGroup: L.LayerGroup | null = null;
  private layerGroups = new Map<string, L.LayerGroup>();
  private wgsCollection: ImportPreviewFeatureCollection | null = null;
  private lastGeometryKey = '';
  private lastVisibilityKey = '';
  private lastRenderKey = '';
  private renderGeneration = 0;
  private map: L.Map | null = null;

  private geoJson: ImportPreviewFeatureCollection | null = null;
  private layers: ImportOverlayLayerMeta[] = [];
  private selectedSourceCrs: ResolvedImportSourceCrs | 'auto' = 'auto';
  private detectedSourceCrs: ResolvedImportSourceCrs | null = null;
  private highlightLayerName: string | null = null;
  private options: ImportLayerMapOverlayOptions = {};

  truncated = false;
  hasGeometry = false;
  unavailableReason: string | null = null;
  isBuildingPreview = false;

  attach(map: L.Map | null, options?: ImportLayerMapOverlayOptions): void {
    if (options) this.options = { ...this.options, ...options };
    if (this.map === map && this.overlayGroup) return;
    this.detach(false);
    this.map = map;
    if (!map) return;
    this.overlayGroup = L.layerGroup().addTo(map);
  }

  detach(removeFromMap = true): void {
    this.renderGeneration++;
    if (this.overlayGroup && this.map && removeFromMap) {
      this.map.removeLayer(this.overlayGroup);
    }
    this.overlayGroup = null;
    this.layerGroups.clear();
    this.wgsCollection = null;
    this.lastGeometryKey = '';
    this.lastVisibilityKey = '';
    this.lastRenderKey = '';
    this.map = null;
    this.hasGeometry = false;
    this.truncated = false;
    this.unavailableReason = null;
    this.isBuildingPreview = false;
  }

  setInputs(input: {
    geoJson: ImportPreviewFeatureCollection | null;
    layers: ImportOverlayLayerMeta[];
    selectedSourceCrs: ResolvedImportSourceCrs | 'auto';
    detectedSourceCrs: ResolvedImportSourceCrs | null;
    highlightLayerName?: string | null;
  }): void {
    this.geoJson = input.geoJson;
    this.layers = input.layers ?? [];
    this.selectedSourceCrs = input.selectedSourceCrs;
    this.detectedSourceCrs = input.detectedSourceCrs;
    if (input.highlightLayerName !== undefined) {
      this.highlightLayerName = input.highlightLayerName;
    }
  }

  setHighlight(layerName: string | null): void {
    this.highlightLayerName = layerName;
    this.applyHighlightStyles();
    this.lastRenderKey = `${this.lastGeometryKey}|hl:${layerName ?? ''}`;
  }

  /**
   * @param options.forceFit — always pan/zoom to painted geometries after render
   *   (used when entering map-hosted mapping / CRS change / master show-all).
   *   Per-layer visibility toggles rebuild paint only — they do not fit unless forceFit.
   */
  async render(options?: { forceFit?: boolean }): Promise<void> {
    if (!this.map || !this.overlayGroup) return;
    const generation = ++this.renderGeneration;
    const forceFit = options?.forceFit === true;
    let paintedNewGeometry = false;
    let visibilityChanged = false;

    if (!this.geoJson?.features?.length) {
      this.hasGeometry = false;
      this.truncated = false;
      this.isBuildingPreview = false;
      this.unavailableReason =
        'Náhled na mapě není k dispozici (chybí GeoJSON — u velkých DXF se náhled neposílá).';
      this.overlayGroup.clearLayers();
      this.layerGroups.clear();
      this.lastGeometryKey = '';
      this.lastVisibilityKey = '';
      this.lastRenderKey = '';
      return;
    }

    const crs = resolveImportPreviewCrs(this.geoJson, this.selectedSourceCrs, this.detectedSourceCrs);
    // CRS + feature count only — visibility must not invalidate the WGS84 cache.
    const geometryKey = `${crs}|${this.geoJson.features.length}`;
    const visibilityKey = this.layers
      .map((l) => `${l.layerName}:${this.isLayerVisible(l) ? 1 : 0}`)
      .join('|');
    const renderKey = `${geometryKey}|vis:${visibilityKey}|hl:${this.highlightLayerName ?? ''}`;

    if (geometryKey !== this.lastGeometryKey || !this.wgsCollection) {
      this.isBuildingPreview = true;
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      if (generation !== this.renderGeneration || !this.overlayGroup) return;

      const built = buildImportPreviewFeatureCollection(this.geoJson, crs, {
        maxFeatures: this.options.maxFeatures,
      });
      if (generation !== this.renderGeneration || !this.overlayGroup) return;

      if (!built || built.collection.features.length === 0) {
        this.hasGeometry = false;
        this.truncated = !!built?.truncated;
        this.isBuildingPreview = false;
        this.unavailableReason =
          'Nepodařilo se převést souřadnice do WGS84 — zkontrolujte souřadnicový systém.';
        this.overlayGroup.clearLayers();
        this.layerGroups.clear();
        this.wgsCollection = null;
        this.lastGeometryKey = geometryKey;
        this.lastVisibilityKey = visibilityKey;
        this.lastRenderKey = renderKey;
        return;
      }

      this.wgsCollection = built.collection;
      this.truncated = built.truncated;
      this.unavailableReason = null;
      this.hasGeometry = true;
      this.paintLayers(built.collection);
      paintedNewGeometry = true;
      this.lastGeometryKey = geometryKey;
      this.lastVisibilityKey = visibilityKey;
      this.isBuildingPreview = false;
    } else if (visibilityKey !== this.lastVisibilityKey) {
      // Visibility changed — re-paint from cached WGS84 (no CRS rebuild).
      this.paintLayers(this.wgsCollection);
      this.lastVisibilityKey = visibilityKey;
      visibilityChanged = true;
    } else if (renderKey !== this.lastRenderKey) {
      this.applyHighlightStyles();
    }

    this.lastRenderKey = renderKey;
    if (paintedNewGeometry || forceFit) {
      // Fit only after WGS84 coords exist; second pass after side-panel / invalidateSize.
      this.scheduleFitAllVisible(generation);
    } else if (visibilityChanged) {
      void Promise.resolve().then(() => this.map?.invalidateSize({ animate: false }));
    }
  }

  /** Immediate + deferred fit so zoom lands after panel layout settles. */
  private scheduleFitAllVisible(generation: number): void {
    this.fitAllVisible({ animate: false });
    window.setTimeout(() => {
      if (generation !== this.renderGeneration) return;
      this.map?.invalidateSize({ animate: false });
      this.fitAllVisible({ animate: true });
    }, 140);
    void Promise.resolve().then(() => this.map?.invalidateSize({ animate: false }));
  }

  fitToLayer(layerName: string): void {
    if (!this.map) return;
    const group = this.layerGroups.get(layerName);
    if (!group) return;
    const fg = L.featureGroup(group.getLayers());
    const b = fg.getBounds();
    if (b.isValid()) {
      this.map.fitBounds(b.pad(0.12), {
        maxZoom: 18,
        animate: true,
        paddingTopLeft: this.options.fitPaddingTopLeft,
        paddingBottomRight: this.options.fitPaddingBottomRight,
      });
    }
  }

  /**
   * Pan/zoom to all currently painted (visible) import geometries.
   * Bounds are taken from leaf GeoJSON layers — nested LayerGroups do not
   * implement getBounds, so wrapping overlayGroup children alone never fits.
   */
  fitAllVisible(options?: { animate?: boolean }): void {
    if (!this.map || !this.overlayGroup) return;
    const leafLayers: L.Layer[] = [];
    for (const group of this.layerGroups.values()) {
      group.eachLayer((layer) => leafLayers.push(layer));
    }
    if (!leafLayers.length) {
      this.map.setView(
        [environment.maps.defaultCenter[0], environment.maps.defaultCenter[1]],
        7,
      );
      return;
    }
    const fg = L.featureGroup(leafLayers);
    const b = fg.getBounds();
    if (b.isValid()) {
      this.map.fitBounds(b.pad(0.08), {
        maxZoom: 17,
        animate: options?.animate !== false,
        paddingTopLeft: this.options.fitPaddingTopLeft ?? [24, 24],
        paddingBottomRight: this.options.fitPaddingBottomRight ?? [24, 24],
      });
    }
  }

  private isLayerVisible(meta: ImportOverlayLayerMeta): boolean {
    if (meta.visible === false || meta.visibleOnMap === false) return false;
    return true;
  }

  private paintLayers(collection: ImportPreviewFeatureCollection): void {
    if (!this.overlayGroup) return;
    this.overlayGroup.clearLayers();
    this.layerGroups.clear();

    const layerNames =
      this.layers.length > 0
        ? this.layers.map((l) => l.layerName)
        : uniqueLayerNames(collection);

    for (let i = 0; i < layerNames.length; i++) {
      const layerName = layerNames[i];
      const meta = this.layers.find((l) => l.layerName === layerName);
      if (meta && !this.isLayerVisible(meta)) continue;

      const layerFc = filterPreviewFeaturesByLayer(collection, layerName);
      if (!layerFc.features.length) continue;

      const color = importLayerPreviewColor(
        this.layers.length ? this.layers.findIndex((l) => l.layerName === layerName) : i,
      );
      const group = L.layerGroup();
      const geoJsonLayer = L.geoJSON(layerFc as GeoJSON.GeoJsonObject, {
        style: () => this.styleForLayer(layerName, color),
        pointToLayer: (_feature, latlng) =>
          L.circleMarker(latlng, {
            radius: this.highlightLayerName === layerName ? 7 : 5,
            color,
            weight: 2,
            fillColor: color,
            fillOpacity: this.highlightLayerName === layerName ? 0.85 : 0.55,
          }),
        onEachFeature: (_feature, layer) => {
          layer.on('click', () => this.options.onLayerActivated?.(layerName));
        },
      });
      geoJsonLayer.addTo(group);
      group.addTo(this.overlayGroup);
      this.layerGroups.set(layerName, group);
    }
  }

  private styleForLayer(layerName: string, color: string): L.PathOptions {
    const highlighted = this.highlightLayerName === layerName;
    const dimmed = !!this.highlightLayerName && !highlighted;
    return {
      color,
      weight: highlighted ? 4 : 2,
      opacity: dimmed ? 0.25 : highlighted ? 1 : 0.85,
      fillColor: color,
      fillOpacity: dimmed ? 0.08 : highlighted ? 0.35 : 0.2,
    };
  }

  private applyHighlightStyles(): void {
    for (const [layerName, group] of this.layerGroups) {
      const idx = this.layers.findIndex((l) => l.layerName === layerName);
      const color = importLayerPreviewColor(idx >= 0 ? idx : 0);
      group.eachLayer((layer) => {
        if (layer instanceof L.GeoJSON) {
          layer.setStyle(this.styleForLayer(layerName, color));
          layer.eachLayer((child) => {
            if (child instanceof L.CircleMarker) {
              child.setStyle({
                radius: this.highlightLayerName === layerName ? 7 : 5,
                color,
                fillColor: color,
                fillOpacity: this.highlightLayerName === layerName ? 0.85 : 0.55,
                opacity: this.highlightLayerName && this.highlightLayerName !== layerName ? 0.25 : 1,
              });
            }
          });
        }
      });
    }
  }

}

function uniqueLayerNames(collection: ImportPreviewFeatureCollection): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of collection.features) {
    const name = importLayerNameFromFeature(f);
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(name);
  }
  return out;
}
